import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    Divider,
    Grid,
    Paper,
    Chip,
    CircularProgress,
    IconButton
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
    Close as CloseIcon,
    Download as DownloadIcon
} from '@mui/icons-material';

/**
 * SignatureStatusModal Component
 *
 * Displays the signature status for both landlord and tenant
 * Features:
 * - Visual signature status indicators
 * - Signature preview images
 * - Timestamp display
 * - Download button for fully signed agreements
 * - Responsive design
 */
const SignatureStatusModal = ({
    open,
    onClose,
    agreement,
    signatureStatus,
    loading = false,
    onDownload
}) => {
    if (!agreement) return null;

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'Not signed yet';
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status color
    const getStatusColor = (signed) => {
        return signed ? 'success' : 'warning';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{
                color: '#398DD2',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Signature Status
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Divider sx={{ mb: 3 }} />

                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Loading signature status...</Typography>
                    </Box>
                ) : (
                    <>
                        {/* Agreement Information */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: '#f5f5f5',
                                borderRadius: 2
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Agreement ID
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                                        {agreement.id}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Agreement Title
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {agreement.template_name || 'Tenancy Agreement'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Status
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            label={agreement.status}
                                            color={signatureStatus?.fully_signed ? 'success' : 'warning'}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="textSecondary">
                                        Overall Status
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                        {signatureStatus?.fully_signed ? (
                                            <span style={{ color: '#4caf50' }}>
                                                Fully Signed
                                            </span>
                                        ) : signatureStatus?.partially_signed ? (
                                            <span style={{ color: '#ff9800' }}>
                                                Partially Signed
                                            </span>
                                        ) : (
                                            <span style={{ color: '#757575' }}>
                                                Unsigned
                                            </span>
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Signature Status Cards */}
                        <Grid container spacing={3}>
                            {/* Landlord Signature */}
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        height: '100%',
                                        border: signatureStatus?.landlord_signature?.signed
                                            ? '2px solid #4caf50'
                                            : '2px solid #ff9800'
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 2
                                    }}>
                                        <Typography variant="h6" fontWeight={600}>
                                            Landlord
                                        </Typography>
                                        <Chip
                                            icon={signatureStatus?.landlord_signature?.signed
                                                ? <CheckCircleIcon />
                                                : <PendingIcon />
                                            }
                                            label={signatureStatus?.landlord_signature?.signed
                                                ? 'Signed'
                                                : 'Pending'
                                            }
                                            color={getStatusColor(signatureStatus?.landlord_signature?.signed)}
                                            size="small"
                                        />
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Signature Timestamp */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="textSecondary">
                                            Signed At
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDate(signatureStatus?.landlord_signature?.signed_at)}
                                        </Typography>
                                    </Box>

                                    {/* Signature Preview */}
                                    {signatureStatus?.landlord_signature?.signature_image ? (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                bgcolor: '#ffffff',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                                                Signature Preview
                                            </Typography>
                                            <img
                                                src={signatureStatus.landlord_signature.signature_image}
                                                alt="Landlord Signature"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '150px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </Paper>
                                    ) : (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                bgcolor: '#f5f5f5',
                                                border: '1px dashed #ccc',
                                                borderRadius: 1,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <PendingIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                            <Typography variant="body2" color="textSecondary">
                                                Awaiting signature
                                            </Typography>
                                        </Paper>
                                    )}
                                </Paper>
                            </Grid>

                            {/* Tenant Signature */}
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        height: '100%',
                                        border: signatureStatus?.tenant_signature?.signed
                                            ? '2px solid #4caf50'
                                            : '2px solid #ff9800'
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 2
                                    }}>
                                        <Typography variant="h6" fontWeight={600}>
                                            Tenant
                                        </Typography>
                                        <Chip
                                            icon={signatureStatus?.tenant_signature?.signed
                                                ? <CheckCircleIcon />
                                                : <PendingIcon />
                                            }
                                            label={signatureStatus?.tenant_signature?.signed
                                                ? 'Signed'
                                                : 'Pending'
                                            }
                                            color={getStatusColor(signatureStatus?.tenant_signature?.signed)}
                                            size="small"
                                        />
                                    </Box>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Signature Timestamp */}
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="textSecondary">
                                            Signed At
                                        </Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDate(signatureStatus?.tenant_signature?.signed_at)}
                                        </Typography>
                                    </Box>

                                    {/* Signature Preview */}
                                    {signatureStatus?.tenant_signature?.signature_image ? (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                bgcolor: '#ffffff',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                                                Signature Preview
                                            </Typography>
                                            <img
                                                src={signatureStatus.tenant_signature.signature_image}
                                                alt="Tenant Signature"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '150px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </Paper>
                                    ) : (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                bgcolor: '#f5f5f5',
                                                border: '1px dashed #ccc',
                                                borderRadius: 1,
                                                textAlign: 'center'
                                            }}
                                        >
                                            <PendingIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                            <Typography variant="body2" color="textSecondary">
                                                Awaiting signature
                                            </Typography>
                                        </Paper>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Next Action Info */}
                        {signatureStatus?.next_action && (
                            <Paper
                                elevation={0}
                                sx={{
                                    mt: 3,
                                    p: 2,
                                    bgcolor: signatureStatus.fully_signed ? '#e8f5e9' : '#fff3e0',
                                    borderRadius: 2,
                                    border: `1px solid ${signatureStatus.fully_signed ? '#4caf50' : '#ff9800'}`
                                }}
                            >
                                <Typography variant="body2" fontWeight={500} color="textPrimary">
                                    Next Steps: {signatureStatus.next_action}
                                </Typography>
                            </Paper>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {signatureStatus?.fully_signed && signatureStatus?.can_download && (
                    <Button
                        startIcon={<DownloadIcon />}
                        onClick={onDownload}
                        variant="contained"
                        sx={{
                            bgcolor: '#4caf50',
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: '#388e3c'
                            }
                        }}
                    >
                        Download Signed Agreement
                    </Button>
                )}
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SignatureStatusModal;
